<template>
    <transition
        name="toast-reveal"
        @after-leave="clearAndRemove"
    >
        <div
            v-show="visible"
            class="toast-holder cursor-pointer bg-white mt-xs ml-auto mr-lg items-center pointer-events-auto p-xs rounded-md"
            :class="[style.class, xAlign]"
            @click.prevent.stop="hide"
        >
            <div class="flex items-center justify-center">
                <div
                    v-if="type != 'info'"
                    class="icon-wrapper flex items-center justify-center"
                    :class="style.class"
                >
                    <icon
                        class="h-6 w-6"
                        :for="style.icon"
                    />
                </div>
                <component
                    :is="component"
                    v-if="component"
                    v-bind="componentConfig"
                />
                <div
                    v-else
                    class="p-xxxs text-charcoal"
                >
                    {{ message }}
                </div>
                <div
                    v-if="autoRemove"
                    class="background-indicator"
                    :style="backgroundStyle"
                />
            </div>
        </div>
    </transition>
</template>
<script>
import Icon from '../../UIToolKit/Icon.vue';

export default {
    name: 'UiToast',
    components: { Icon },
    props: {
        message: {
            type: String,
            default: 'New Message',
        },
        showTime: {
            type: Number,
            default: 3000,
        },
        type: {
            type: String,
            default: 'info',
        },
        xAlign: {
            type: String,
            default: 'right',
        },
        component: {
            type: String,
            default: null,
        },
        componentConfig: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            resolve: null,
            reject: null,
            rate: 50,
            interval: null,
            visible: false,
            autoRemove: true,
            styles: {
                error: {
                    class: 'error',
                    icon: 'cross-c-f',
                },
                warning: {
                    class: 'warning text-mandarine',
                    icon: 'warning-filled',
                },
                success: {
                    class: 'success',
                    icon: 'tick-sml',
                },
                info: {
                    class: 'flex-col info pl-md pr-md',
                },
            },
            backgroundStyle: {
                height: '100%',
            },
        };
    },
    computed: {
        style() {
            return this.styles[this.type] || this.styles.info;
        },
    },
    mounted() {
        if (this.type === 'error') {
            this.autoRemove = false;
        }
    },
    methods: {
        show() {
            this.promise = new Promise((resolve, reject) => {
                let timer = 0;
                this.resolve = resolve;
                this.reject = reject;
                this.visible = true;

                if (this.autoRemove) {
                    this.interval = setInterval(() => {
                        timer += this.rate;
                        if (timer >= this.showTime) {
                            this.hide();
                        }

                        this.backgroundStyle = {
                            height: `${(timer / this.showTime) * 100}%`,
                        };
                    }, this.rate);
                }
            });

            return this.promise;
        },
        hide() {
            clearInterval(this.interval);
            this.visible = false;
        },
        clearAndRemove() {
            this.resolve(true);
            clearInterval(this.interval);
        },
    },
};
</script>
<style lang="scss">
    @import 'common/scss/variables';
    @import "common/scss/palette";
    @import 'common/scss/shadows';
    @import "common/scss/mixins";

    $z-base: 800;
    $top-offset: 60px;
    $right-offset: 40px;
    $min-height: 40px;
    $box-size: 4px;
    $cross-size: 12px;

    .toast-holder {
        display: table;
        transition: 0.3s all ease;
        @include shadow-bottom(8px);
        transform: translateX(0px);
        min-width: 120px;
        z-index: 780;
        border-radius: $border-curve;
        border: $border-sm $light-gray solid;

        .background-indicator {
            @apply absolute w-full;
            opacity: 0.1;
            top: 0;
            right: 0;
            transition: 0.1s all ease;
        }

        &.success {
            .icon-wrapper {
                @apply rounded-full w-5 h-5 m-xxxs bg-leaf text-white;
            }
        }

        &.error {
            .icon-wrapper {
                @apply w-6 h-6 text-grape;
            }
        }

        @for $i from 1 through 10 {
            &.left:nth-child(#{$i}) {
                left: $right-offset;
                z-index: $z-base - $i;
            }
        }

        @for $i from 1 through 10 {
            &.right:nth-child(#{$i}) {
                right: $right-offset;
                z-index: $z-base - $i;
            }
        }

        @for $i from 1 through 10 {
            &.center:nth-child(#{$i}) {
                z-index: $z-base - $i;
                margin: 0 auto;

                > div {
                    @include truncatable('inline-block');
                }
            }
        }

        &.left.toast-reveal-enter {
            transform: translate(-70px, 30px) scale(0.7);
            opacity: 0;
        }

        &.left.toast-reveal-leave-to {
            transform: translateX(-100px) scale(0.7);
            opacity: 0;
        }

        &.right.toast-reveal-enter {
            transform: translate(70px, 30px) scale(0.7);
            opacity: 0;
        }

        &.right.toast-reveal-leave-to {
            transform: translateX(100px) scale(0.7);
            opacity: 0;
        }

        &.center.toast-reveal-enter {
            transform: translateY(-70px) scale(1.1);
            opacity: 0;
        }

        &.center.toast-reveal-leave-to {
            transform: translateY(-100px) scale(1.1);
            opacity: 0;
        }

        .cross {
            position: relative;
            width: $cross-size;
            height: $cross-size;
            margin-left: auto;

            &:after, &:before {
                content: "";
                width: 100%;
                height: 2px;
                border-radius: 2px;
                position: absolute;
                top: 50%;
                background: $background-gray;
            }

            &:after {
                transform: rotate(62deg);
                left: 0;
            }

            &:before {
                transform: rotate(-62deg);
                right: 0;
            }
        }

        .boxes {
            width: $box-size;
            height: $box-size;
            background: $background-gray;
            margin: calc(#{$cross-size} - #{$box-size}) $box-size;
            margin-left: auto;

            &:after, &:before {
                content: "";
                width: $box-size;
                height: $box-size;
                background: $background-gray;
                position: absolute;
            }

            &:after {
                top: calc(50% - #{$box-size * 2});
            }

            &:before {
                top: calc(50% + #{$box-size});
            }
        }
    }

    .toast_center {
        display: flex;
        flex-direction: column;
        align-items: center;
    }
</style>
